import { useMutation, useQuery } from "@tanstack/react-query";
import { useAssessmentStore } from "../store/assessment.store";
import { apiService } from "../services/apiService";
import type { AssessmentAttempt, Question, Result, UserAnswer } from "../types/assessment.types";
import { useAuthStore } from "../store/authStore";

export const useAssessment = () => {
    const { user } = useAuthStore(); // Usar hook reativo em vez de getState()
    const { actions } = useAssessmentStore();

    const { data: questions, isLoading: isLoadingQuestions, error: questionsError } = useQuery<Question[], Error>({
        queryKey: ["assessmentQuestions"],
        queryFn: apiService.getQuestions,
        staleTime: Infinity,
        cacheTime: Infinity, // Mantém no cache indefinidamente
        refetchOnMount: false, // Não refetch ao montar
        refetchOnWindowFocus: false, // Não refetch ao focar janela
        enabled: !!user,
    });

    const { mutate: startMutation, isPending: isStarting } = useMutation<AssessmentAttempt, Error>({
        mutationFn: apiService.startAssessment,
        onSuccess: (data) => {
            if (questions) {
                // Check if this is a resumed assessment (has answers)
                if (data.Answers && data.Answers.length > 0) {
                    const progress = Math.floor((data.Answers.length / questions.length) * 100);
                    actions.resume(data.ID, questions, data.Answers, progress);
                } else {
                    actions.start(data.ID, questions);
                }
            }
        },
        onError: (error) => {
            if (error.message === 'ASSESSMENT_ALREADY_COMPLETED') {
                // Set a specific status for already completed assessments
                actions.setStatus('already_completed');
            } else {
                console.error("Failed to start assessment:", error);
                actions.setStatus('error');
            }
        }
    });

    const { mutate: submitMutation, isPending: isSubmitting } = useMutation<any, Error, { assessmentId: string; answers: UserAnswer[]; onSuccess?: () => void }>({
        mutationFn: (variables) => apiService.submitResponse(variables.assessmentId, variables.answers),
        onMutate: () => actions.setSaving(),
        onSuccess: (_data, variables) => {
            actions.setSavingComplete();
            if (variables.onSuccess) {
                variables.onSuccess();
            } else {
                actions.nextQuestion();
            }
        },
        onError: (error) => {
            console.error("Failed to submit answers:", error);
            actions.setStatus('error');
        }
    });

    const { mutate: finalizeMutation, isPending: isFinalizing } = useMutation<Result, Error, string>({
        mutationFn: apiService.finalizeAssessment,
        onMutate: () => actions.setFinalizing(),
        onSuccess: (data) => {
            actions.complete(data);
        },
        onError: (error) => {
            console.error("Failed to finalize assessment:", error);
            actions.setStatus('error');
        }
    });

    // A função retornada não precisa de argumentos, o token resolve o usuário
    const handleStart = () => {
        if (questions) {
            startMutation();
        }
    };

    const handleSubmitAnswer = (assessmentId: string, answer: UserAnswer, options?: { onSuccess?: () => void }) => {
        // Envia apenas a resposta atual, não todas as respostas
        submitMutation({ assessmentId, answers: [answer], onSuccess: options?.onSuccess });
    };

    return {
        questions,
        isLoadingQuestions,
        questionsError,
        startAssessment: handleStart, // <- Note que é o handleStart
        isStarting,
        submitAnswer: handleSubmitAnswer,
        isSubmitting,
        finalizeAssessment: finalizeMutation,
        isFinalizing,
    };
};

// New hook for checking user assessment status
export const useAssessmentStatus = () => {
    const { user } = useAuthStore(); // Usar hook reativo em vez de getState()
    
    return useQuery({
        queryKey: ["userAssessmentStatus", user?.id],
        queryFn: apiService.getUserAssessmentStatus,
        enabled: !!user,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes cache
        refetchOnWindowFocus: false, // Não refetch ao focar janela
    });
}; 